import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, MoreVertical, Pencil, UserPlus, Archive, ArchiveRestore, Loader2, X, Save, Building2, ExternalLink } from "lucide-react";
import api, { BACKEND_URL } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ChatMessage from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import MediaLightbox from "./MediaLightbox";
import AddMembersModal from "./AddMembersModal";
import MessageActionsMenu from "./MessageActionsMenu";
import ConvertMessageDialog from "./ConvertMessageDialog";
import { toast } from "../ui/sonner";

/**
 * ChatThreadView — Wave 19 #85.
 *
 * Renders one chat thread + composer. Reused by:
 *   - /chat/:tid   (full-screen page)
 *   - <EntityChatPanel> (embedded on JC/Ticket/Task detail pages)
 *
 * Polls every 3 seconds (visibility-gated) for new messages. Optimistic
 * local rendering on send keeps the UX feeling instant.
 */

function _isSameAuthor(a, b) {
  if (!a || !b) return false;
  if (a.author_id && b.author_id) return a.author_id === b.author_id;
  return false;
}

// Wave 23 — group consecutive image/video messages from the same author
// within `windowMs` into a single "burst" so they render as a gallery.
// Returns an array where each entry is either:
//   { type: "single", msg }   — render as one bubble
//   { type: "burst", anchor, items }
//      — render `anchor` (= items[0]) as a gallery bubble; skip items[1..]
const BURST_WINDOW_MS = 60_000;
function _groupBursts(messages) {
  const out = [];
  for (let i = 0; i < messages.length; i++) {
    const m = messages[i];
    const isMedia = m.kind === "image" || m.kind === "video";
    if (!isMedia) { out.push({ type: "single", msg: m }); continue; }
    // Try to extend a burst forward
    const items = [m];
    let j = i + 1;
    while (j < messages.length) {
      const n = messages[j];
      const sameKindFamily = n.kind === "image" || n.kind === "video";
      if (!sameKindFamily) break;
      if (!_isSameAuthor(m, n)) break;
      const dt = (new Date(n.created_at) - new Date(items[items.length - 1].created_at)) || 0;
      if (Math.abs(dt) > BURST_WINDOW_MS) break;
      items.push(n);
      j += 1;
    }
    if (items.length > 1) {
      out.push({ type: "burst", anchor: m, items });
      i = j - 1; // skip burst tail
    } else {
      out.push({ type: "single", msg: m });
    }
  }
  return out;
}

export default function ChatThreadView({ threadId, embedded = false }) {
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [showMembers, setShowMembers] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [lightboxId, setLightboxId] = useState(null);
  // Wave 19 #94 — message-action menu + conversion modal state.
  const [actionsAnchor, setActionsAnchor] = useState(null); // { msg, x, y }
  const [conversionTarget, setConversionTarget] = useState(null); // { msg, target }
  const scrollRef = useRef(null);
  const lastMsgIsoRef = useRef("");

  const loadThread = useCallback(async () => {
    try {
      const { data } = await api.get(`/chats/${threadId}`);
      setThread(data);
      setNewTitle(data?.title || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load chat.");
    }
  }, [threadId]);

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/chats/${threadId}/messages`);
      setMessages(data || []);
      lastMsgIsoRef.current = data?.length ? data[data.length - 1].created_at : "";
    } catch (e) {
      // silent on poll
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  // Incremental poll using `since=` to keep payloads small.
  const pollTick = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const since = lastMsgIsoRef.current;
      const { data } = await api.get(`/chats/${threadId}/messages${since ? `?since=${encodeURIComponent(since)}` : ""}`);
      if (data && data.length > 0) {
        setMessages((rows) => {
          const seen = new Set(rows.map((m) => m.id));
          const merged = [...rows];
          for (const m of data) {
            if (!seen.has(m.id)) merged.push(m);
          }
          return merged;
        });
        lastMsgIsoRef.current = data[data.length - 1].created_at;
      }
      // Always also refresh existing messages so transcription updates land.
      // Cheap because we use `since=` for the bulk; only refetch periodically.
    } catch { /* noop */ }
  }, [threadId]);

  // Periodic full refresh (every 15s) catches transcription updates on
  // existing audio messages.
  const refreshExisting = useCallback(async () => {
    if (document.visibilityState !== "visible") return;
    try {
      const { data } = await api.get(`/chats/${threadId}/messages?limit=200`);
      if (data) {
        setMessages(data);
        lastMsgIsoRef.current = data.length ? data[data.length - 1].created_at : "";
      }
    } catch { /* noop */ }
  }, [threadId]);

  useEffect(() => {
    setLoading(true);
    loadThread();
    loadMessages();
  }, [loadThread, loadMessages]);

  useEffect(() => {
    const fast = setInterval(pollTick, 3_000);
    const slow = setInterval(refreshExisting, 15_000);
    return () => { clearInterval(fast); clearInterval(slow); };
  }, [pollTick, refreshExisting]);

  // Iter 393 — Boost polling while any voice note is still being
  // transcribed so the transcription lands under the audio bubble
  // within a few seconds of Whisper finishing (typical: 2-6s).
  useEffect(() => {
    const anyPending = messages.some((m) => m.kind === "audio" && m.transcription_status === "pending");
    if (!anyPending) return undefined;
    const boost = setInterval(refreshExisting, 2_500);
    return () => clearInterval(boost);
  }, [messages, refreshExisting]);

  // Mark seen on mount + whenever new messages arrive while in foreground
  useEffect(() => {
    if (!threadId) return;
    api.post(`/chats/${threadId}/seen`).catch(() => {});
  }, [threadId, messages.length]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const submitRename = async () => {
    if (!newTitle.trim()) { setRenaming(false); return; }
    try {
      await api.put(`/chats/${threadId}/rename`, { title: newTitle.trim() });
      await loadThread();
      await loadMessages();
      setRenaming(false);
      toast.success("Renamed.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rename.");
    }
  };

  const toggleArchive = async () => {
    try {
      await api.post(`/chats/${threadId}/${thread?.archived ? "unarchive" : "archive"}`);
      await loadThread();
      toast.success(thread?.archived ? "Restored to active." : "Archived.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    }
    setShowMenu(false);
  };

  if (loading) {
    return (
      <div className="hv-card p-6 text-sm text-[#a0aabf] flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!thread) {
    return <div className="hv-card p-6 text-sm text-[#a0aabf]">Chat not found.</div>;
  }

  const memberNames = (thread.members || []).map((m) => m.name).filter(Boolean);
  const memberLine = memberNames.slice(0, 4).join(", ") + (memberNames.length > 4 ? `, +${memberNames.length - 4}` : "");

  // Wave 19 #92 — header navigation pills.
  const entityHref = (() => {
    const k = thread.kind;
    const eid = thread.entity_id;
    if (!eid) return null;
    if (k === "jobcard") return `/jobcards/${eid}`;
    if (k === "ticket") return `/tickets/${eid}`;
    if (k === "task") return `/tasks/${eid}`;
    return null;
  })();
  const entityLabel = (() => {
    const k = thread.kind;
    if (k === "jobcard") return "Open job card";
    if (k === "ticket") return "Open ticket";
    if (k === "task") return "Open task";
    return null;
  })();
  const entityShort = thread.entity_short_id;

  return (
    <div
      className={`flex flex-col ${
        embedded
          ? "h-[600px] hv-card overflow-hidden"
          : "h-full hv-card overflow-hidden rounded-none md:rounded"
      }`}
      data-testid="chat-thread-view"
    >
      {/* Header — sticky inside the scroll container so the title is
          always visible even on tall message lists. */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-white/10 bg-[#0c1018]/95 backdrop-blur sticky top-0 z-20 shrink-0">
        {!embedded && (
          <Link to="/chat" className="hv-btn hv-btn-ghost text-xs" data-testid="chat-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Link>
        )}
        <div className="flex-1 min-w-0">
          {renaming ? (
            <div className="flex items-center gap-2">
              <input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                className="hv-input text-sm flex-1"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") submitRename(); if (e.key === "Escape") setRenaming(false); }}
                data-testid="chat-rename-input"
              />
              <button onClick={submitRename} className="hv-btn hv-btn-primary text-xs" data-testid="chat-rename-save">
                <Save className="w-3 h-3" />
              </button>
              <button onClick={() => setRenaming(false)} className="hv-btn hv-btn-ghost text-xs">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="space-y-1">
              <button
                type="button"
                onClick={() => setShowMembers((v) => !v)}
                className="text-left w-full block"
                data-testid="chat-header-info"
              >
                <div className="text-sm font-bold text-white truncate flex items-center gap-2">
                  {thread.title}
                  {thread.archived && (
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-400/40 text-amber-200">
                      Archived
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-[#a0aabf] truncate">
                  {memberLine || `${(thread.member_ids || []).length} members`}
                </div>
              </button>
              {/* Navigation pills — friendlier teal accents. */}
              {(thread.customer_name || entityHref) && (
                <div className="flex flex-wrap items-center gap-1.5 pt-0.5" data-testid="chat-header-pills">
                  {thread.customer_name && thread.customer_id && (
                    <Link
                      to={`/customers/${thread.customer_id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#1fb6c1]/12 border border-[#1fb6c1]/35 text-[#7fe5ec] hover:bg-[#1fb6c1]/20 hover:text-white transition"
                      data-testid="chat-header-customer-link"
                    >
                      <Building2 className="w-3 h-3" />
                      <span className="truncate max-w-[180px]">{thread.customer_name}</span>
                    </Link>
                  )}
                  {entityHref && (
                    <Link
                      to={entityHref}
                      className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[#cbd2df] hover:bg-white/10 hover:text-white transition"
                      data-testid="chat-header-entity-link"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {entityShort ? <span>{entityShort}</span> : null}
                      <span>{entityLabel}</span>
                    </Link>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu((v) => !v)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="chat-menu-btn"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {showMenu && (
            // iter 186 (2026-05-22, Dirk) — `word-break: break-word` from
            // the mobile-overflow guard in index.css was breaking the
            // menu items letter-by-letter on narrow JC chat embeds
            // (parent panel ~280px). `whitespace-nowrap` on each item +
            // min-w-[14rem] on the menu container fix it without
            // weakening the global overflow guard.
            <div className="absolute right-0 top-full mt-1 hv-card p-1 z-50 w-56 min-w-[14rem] shadow-2xl" data-testid="chat-menu">
              <button
                onClick={() => { setRenaming(true); setShowMenu(false); }}
                className="hv-btn hv-btn-ghost text-xs w-full justify-start whitespace-nowrap"
                data-testid="chat-menu-rename"
              >
                <Pencil className="w-3 h-3" /> Rename
              </button>
              <button
                onClick={() => { setShowAddMembers(true); setShowMenu(false); }}
                className="hv-btn hv-btn-ghost text-xs w-full justify-start whitespace-nowrap"
                data-testid="chat-menu-add-members"
              >
                <UserPlus className="w-3 h-3" /> Add members
              </button>
              <button
                onClick={() => { setShowMembers(true); setShowMenu(false); }}
                className="hv-btn hv-btn-ghost text-xs w-full justify-start whitespace-nowrap"
                data-testid="chat-menu-members"
              >
                <UserPlus className="w-3 h-3" /> Show members
              </button>
              <button
                onClick={toggleArchive}
                className="hv-btn hv-btn-ghost text-xs w-full justify-start whitespace-nowrap"
                data-testid="chat-menu-archive"
              >
                {thread.archived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                {thread.archived ? "Restore" : "Archive"}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Member sidebar (slide-in) — soft teal for self, neutral for others. */}
      {showMembers && (
        <div className="border-b border-white/10 bg-white/[0.02] p-3" data-testid="chat-member-list">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">
              {(thread.member_ids || []).length} members
            </div>
            <button
              onClick={() => { setShowAddMembers(true); }}
              className="text-[10px] uppercase tracking-[0.18em] text-[#1fb6c1] hover:text-white transition inline-flex items-center gap-1"
              data-testid="chat-member-add-inline"
            >
              <UserPlus className="w-3 h-3" /> Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(thread.members || []).map((m) => (
              <div
                key={m.id}
                className={`text-xs px-2 py-1 rounded border ${
                  m.id === user?.id
                    ? "bg-[#1fb6c1]/12 border-[#1fb6c1]/40 text-[#7fe5ec]"
                    : "bg-white/[0.04] border-white/10 text-[#cbd2df]"
                }`}
                data-testid={`chat-member-${m.id}`}
              >
                {m.name} <span className="text-[9px] text-[#647087] uppercase ml-1">{m.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-2 bg-[#0a0d14]" data-testid="chat-messages">
        {messages.length === 0 ? (
          <div className="text-center text-xs text-[#647087] py-8">
            No messages yet. Say hi 👋
          </div>
        ) : (
          (() => {
            const grouped = _groupBursts(messages);
            return grouped.map((entry, gi) => {
              const m = entry.type === "burst" ? entry.anchor : entry.msg;
              const prevEntry = gi > 0 ? grouped[gi - 1] : null;
              const prev = prevEntry ? (prevEntry.type === "burst" ? prevEntry.items[prevEntry.items.length - 1] : prevEntry.msg) : null;
              const isFirstInGroup = !prev || !_isSameAuthor(prev, m) || prev.kind === "system" || m.kind === "system";
              return (
                <ChatMessage
                  key={m.id}
                  msg={m}
                  burst={entry.type === "burst" ? entry.items : undefined}
                  isMine={m.author_id === user?.id}
                  isFirstInGroup={isFirstInGroup}
                  members={thread?.members || []}
                  onMediaClick={(target) => setLightboxId(target.id)}
                  onActionsRequested={(payload) => setActionsAnchor(payload)}
                />
              );
            });
          })()
        )}
      </div>

      {/* Composer (disabled on archived chats) */}
      <ChatComposer
        threadId={threadId}
        onSent={(msg) => {
          setMessages((rows) => [...rows, msg]);
          lastMsgIsoRef.current = msg.created_at;
        }}
        disabled={thread.archived}
      />
      {thread.archived && (
        <div className="text-center text-[10px] text-amber-200 bg-amber-500/[0.05] border-t border-amber-500/20 py-1.5">
          This chat is archived (parent {thread.kind} closed). Restore to send messages.
        </div>
      )}

      {/* Wave 19 #88 — full-screen image / video viewer with arrow scroll */}
      {lightboxId && (
        <MediaLightbox
          items={messages
            .filter((m) => m.kind === "image" || m.kind === "video")
            .map((m) => {
              // [Iter 323] Rebuild the media URL from `upload_id` +
              // live token so stale `?auth=` tokens baked into
              // `upload_url` (at send-time) can't break the viewer.
              const tok = localStorage.getItem("hv_token") || "";
              const src = m.upload_id
                ? `${BACKEND_URL || ""}/api/uploads/${m.upload_id}${tok ? `?auth=${encodeURIComponent(tok)}` : ""}`
                : (m.upload_url || "");
              return {
                msgId: m.id,
                url: src,
                filename: m.upload_filename || (m.kind === "image" ? "image" : "video"),
                kind: m.kind,
                uploadId: m.upload_id,
              };
            })}
          activeId={lightboxId}
          onClose={() => setLightboxId(null)}
        />
      )}

      {/* Wave 19 #92 — friendlier add-members modal (replaces inline editor) */}
      {showAddMembers && (
        <AddMembersModal
          thread={thread}
          onClose={() => setShowAddMembers(false)}
          onAdded={() => {
            loadThread();
            loadMessages();
          }}
        />
      )}

      {/* Wave 19 #94 — right-click / long-press conversion menu */}
      {actionsAnchor && (
        <MessageActionsMenu
          x={actionsAnchor.x}
          y={actionsAnchor.y}
          msg={actionsAnchor.msg}
          hasText={!!(actionsAnchor.msg?.text || actionsAnchor.msg?.transcription)}
          canEdit={
            !actionsAnchor.msg?.deleted_at &&
            (actionsAnchor.msg?.kind || "text") === "text" &&
            actionsAnchor.msg?.author_id === user?.id
          }
          canDelete={
            !actionsAnchor.msg?.deleted_at &&
            (actionsAnchor.msg?.author_id === user?.id ||
              user?.role === "owner" ||
              user?.role === "admin")
          }
          onPick={(target) => {
            setConversionTarget({ msg: actionsAnchor.msg, target });
            setActionsAnchor(null);
          }}
          onCopy={async () => {
            const txt = actionsAnchor.msg?.text || actionsAnchor.msg?.transcription || "";
            try {
              await navigator.clipboard.writeText(txt);
              toast.success("Copied to clipboard.");
            } catch {
              toast.error("Could not copy.");
            }
            setActionsAnchor(null);
          }}
          onEdit={async () => {
            const msg = actionsAnchor.msg;
            setActionsAnchor(null);
            if (!msg) return;
            // Simple prompt-based inline edit — small enough for a
            // long-press menu, and matches the "quick tweak my typo"
            // use case Dirk asked for. A future iteration can promote
            // this to a proper composer-inline editor.
            const next = window.prompt("Edit message", msg.text || "");
            if (next == null) return;
            const trimmed = next.trim();
            if (!trimmed) {
              toast.error("Message text cannot be empty.");
              return;
            }
            if (trimmed === (msg.text || "").trim()) return;
            try {
              const { data } = await api.patch(
                `/chats/${threadId}/messages/${msg.id}`,
                { text: trimmed },
              );
              setMessages((cur) =>
                cur.map((m) => (m.id === msg.id ? { ...m, ...data } : m)),
              );
              toast.success("Message updated.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not edit message.");
            }
          }}
          onDelete={async () => {
            const msg = actionsAnchor.msg;
            setActionsAnchor(null);
            if (!msg) return;
            if (!window.confirm("Delete this message? This cannot be undone.")) return;
            try {
              await api.delete(`/chats/${threadId}/messages/${msg.id}`);
              setMessages((cur) =>
                cur.map((m) =>
                  m.id === msg.id
                    ? { ...m, deleted_at: new Date().toISOString(), text: "" }
                    : m,
                ),
              );
              toast.success("Message deleted.");
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not delete message.");
            }
          }}
          onClose={() => setActionsAnchor(null)}
        />
      )}

      {/* Wave 19 #94 — convert-to-entity modal */}
      {conversionTarget && (
        <ConvertMessageDialog
          thread={thread}
          message={conversionTarget.msg}
          target={conversionTarget.target}
          onClose={(result) => {
            setConversionTarget(null);
            if (result?.created) {
              // Refresh messages so the system breadcrumb bubble appears.
              loadMessages();
            }
          }}
        />
      )}
    </div>
  );
}
